import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
} from "@mui/material";
import { Edit, Save, Delete, Cancel, Add } from "@mui/icons-material";
import useNotification from "../hooks/useNotification";
import { useConsumerApi } from "../hooks/useConsumerApi";
import NotificationSnackbarV1 from "../utils/NotificationSnackbarV1";
import { useNotificationContext } from "../contextProviders/NotificationProvider";
import { DataGrid } from "@mui/x-data-grid";
import { formatLocalDate } from "../utils/formatDateTime";

// Helper function to extract first name from full name
// Handles: Regular users (firstName only) and SSO users (full name in firstName field)
const getFirstName = (name) => {
  if (!name) return "";
  return name.split(" ")[0] || "";
};

const ConsumerDetails = ({ consumer }) => {
  const [newNote, setNewNote] = useState("");
  const [notes, setNotes] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  // const { showNotification } = useNotification();
  const { showNotification } = useNotificationContext();
  const {
    getConsumerCustomFields,
    createConsumerCustomField,
    updateConsumerCustomField,
    deleteConsumerCustomField,
    getConsumerNotesByConsumerID,
    addNoteToConsumer,
    updateConsumerNote
  } = useConsumerApi();

  // Fetch custom fields
  useEffect(() => {
    const fetchFields = async () => {
      if (!consumer?.id) return;

      const result = await getConsumerCustomFields(consumer.id);
      if (result) {
        const editableFields = result.map((f) => ({
          id: f.id,
          key: f.fieldKey,
          value: f.fieldValue,
          isEditing: false,
        }));
        setCustomFields(editableFields);
      }
    };

    fetchFields();
  }, [consumer?.id, getConsumerCustomFields]);

  const fetchNotes = async () => {
    if (!consumer?.id) return;

    const result = await getConsumerNotesByConsumerID(consumer.id);
    if (result && result.length > 0) {
      const formattedNotes = result.map((note) => ({
        id: note.id,
        text: note.content || note.note || "",
        addedBy: note.role || "Operator",
        addedAt: formatDate(note.date) || formatDate(note.createdAt),
      }));
      setNotes(formattedNotes);
    }
  };

  // Fetch notes
  useEffect(() => {
    fetchNotes();
  }, [consumer?.id, getConsumerNotesByConsumerID]);

  // Handle field changes
  const handleFieldChange = (id, key, value) => {
    setCustomFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, key, value } : f))
    );
  };

  // Toggle edit mode for fields
  // const toggleEdit = (id) => {
  //   setCustomFields((prev) =>
  //     prev.map((f) => (f.id === id ? { ...f, isEditing: !f.isEditing } : f))
  //   );
  // };
  const toggleEdit = (id, reset = false) => {
    setCustomFields((prev) =>
      prev.map((f) => {
        if (f.id === id) {
          if (!f.isEditing) {
            // Entering edit mode: store original value
            return { ...f, isEditing: true, originalValue: f.value };
          } else {
            // Exiting edit mode: optionally reset value
            return {
              ...f,
              isEditing: false,
              value: reset ? f.originalValue : f.value,
              originalValue: undefined,
            };
          }
        }
        return f;
      })
    );
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showNotification("Please enter a note", "warning");
      return;
    }

    if (newNote.length > 160) {
      showNotification("Note must be 160 characters or less", "error");
      return;
    }

    if (!consumer?.id) {
      showNotification("Consumer ID not available", "error");
      return;
    }

    const storedUser = JSON.parse(localStorage.getItem("user"));

    const newEntry = {
      date: new Date().toISOString(),
      role: storedUser?.name || storedUser?.role,
      content: newNote.trim(),
      consumerId: consumer.id,
    };

    const result = await addNoteToConsumer(consumer.id, newEntry);

    if (result && !result.error) {
      // Add to local state immediately
      const note = {
        id: result.id || Date.now(),
        text: newNote.trim(),
        addedBy: storedUser?.name || storedUser?.role,
        addedAt: new Date().toISOString(),
      };

      setNotes((prev) => [note, ...prev]);
      setNewNote("");
      fetchNotes();
      showNotification("Note added successfully", "success");
    } else {
      console.error("Failed to add note:", result?.error);
      showNotification("Failed to add note", "error");
    }
  };

  const handleSaveField = async (id) => {
    const field = customFields.find((f) => f.id === id);
    if (!field) return;

    if (!field.value || !field.value.trim()) {
      showNotification("Field value cannot be empty", "warning");
      return;
    }

    const result = await updateConsumerCustomField(id, {
      consumerId: consumer.id,
      fieldKey: field.key,
      fieldValue: field.value.trim(),
    });

    if (!result.error) {
      // Update local state
      setCustomFields((prev) =>
        prev.map((f) => (f.id === id ? { ...f, value: field.value.trim() } : f))
      );
      toggleEdit(id);
      showNotification("Field updated successfully", "success");
    } else {
      showNotification("Failed to update field", "error");
    }
  };

  const handleDeleteField = async (id) => {
    const result = await deleteConsumerCustomField(id);
    if (result && !result.error) {
      setCustomFields((prev) => prev.filter((f) => f.id !== id));
      showNotification("Field deleted successfully", "success");
    } else {
      showNotification("Failed to delete field", "error");
    }
  };

  const handleAddNewField = async () => {
    if (!newFieldKey.trim() || !newFieldValue.trim()) {
      showNotification("Both key and value are required", "warning");
      return;
    }

    if (customFields.some((field) => field.key === newFieldKey.trim())) {
      showNotification("Field with this key already exists", "error");
      return;
    }

    const result = await createConsumerCustomField(consumer.id, {
      consumerId: consumer.id,
      fieldKey: newFieldKey.trim(),
      fieldValue: newFieldValue.trim(),
    });

    if (result && !result.error) {
      const newField = {
        id: result.id || Date.now(),
        key: newFieldKey.trim(),
        value: newFieldValue.trim(),
        isEditing: false,
      };

      setCustomFields((prev) => [...prev, newField]);
      setNewFieldKey("");
      setNewFieldValue("");
      setShowAddField(false);
      showNotification("Field added successfully", "success");
    } else {
      showNotification("Failed to add field", "error");
    }
  };

  const handleCancelAdd = () => {
    setNewFieldKey("");
    setNewFieldValue("");
    setShowAddField(false);
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const years = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${years}-${month}-${day}`;
  };

  if (!consumer) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography>Loading consumer details...</Typography>
      </Box>
    );
  }

  //Table implementation for Notes
  const columns = [
    { field: "id", headerName: "id", width: 70, hide: true },
    {
      field: "addedAt",
      headerName: "Date",
      width: 96,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: "normal" }}>
          <Typography fontWeight={500} variant="caption">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "text",
      headerName: "Note",
      width: 240,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: "normal" }}>
          <Typography variant="caption">{params.value}</Typography>
        </Box>
      ),
    },
  ];

  // Custom Fields columns for DataGrid
  const customFieldColumns = [
    { field: "id", headerName: "id", width: 70, hide: true },
    {
      field: "key",
      headerName: "Key",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: "normal" }}>
          <Typography fontWeight={500} variant="caption">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "value",
      headerName: "Value",
      width: 120,
      renderCell: (params) => {
        const field = customFields.find((f) => f.id === params.row.id);
        if (field?.isEditing) {
          return (
            <TextField
              size="small"
              value={field.value}
              onChange={(e) =>
                handleFieldChange(field.id, field.key, e.target.value)
              }
              onKeyDown={(e) => e.stopPropagation()}
              required
              inputProps={{ maxLength: 30, style: { fontSize: "12px" } }}
              sx={{ maxWidth: "100px" }}
              variant="outlined"
            />
          );
        }
        return (
          <Box sx={{ whiteSpace: "normal", lineHeight: "normal" }}>
            <Typography variant="caption">{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const field = customFields.find((f) => f.id === params.row.id);
        if (field?.isEditing) {
          return (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start", height: "100%", pt: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleSaveField(field.id)}
                color="primary"
              >
                <Save fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteField(field.id)}
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => toggleEdit(field.id, true)}
                color="default"
              >
                <Cancel fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "flex-start", height: "100%", pt: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => toggleEdit(params.row.id)}
              color="primary"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {/* Consumer Info */}
        <div sx={{ mb: 2 }}>
          <div style={{ marginBottom: "20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                variant="outlined"
                size="medium"
                sx={{
                  minWidth: "100px",
                  fontWeight: "bold",
                
                  color: "var(--color-primary)",
                }}
              >
                Consumer Details
              </Typography>
            </Box>
            <Box sx={{ mb: 1, display: "flex" }}>
              <Typography
                variant="body2"
                sx={{ minWidth: "100px", fontWeight: "bold" }}
              >
                Name
              </Typography>
              <Typography variant="body2">{getFirstName(consumer.firstName)}</Typography>
            </Box>
            <Box sx={{ mb: 1, display: "flex" }}>
              <Typography
                variant="body2"
                sx={{ minWidth: "100px", fontWeight: "bold" }}
              >
                DOB
              </Typography>
              <Typography variant="body2">
                {formatLocalDate(consumer.dateOfBirth)}
              </Typography>
            </Box>
          </div>
          <div style={{ marginBottom: "20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ minWidth: "100px", fontWeight: "bold", color: "var(--color-primary)" }}
              >
                Custom Fields
              </Typography>
            </Box>

            {/* Custom Fields Table */}
            <Paper
              sx={{
                maxHeight: 164,
                width: "100%",
                boxShadow: 2,
                mb: 1,
                overflow: "auto",
              }}
            >
              <DataGrid
                rows={customFields}
                columns={customFieldColumns}
                autoHeight
                getRowHeight={() => 'auto'}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      id: false,
                    },
                  },
                  pagination: false,
                }}
                pagination={false}
                disableRowSelectionOnClick
                hideFooter
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "14px",
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-cell": {
                    py: 0.1,
                    minHeight: "auto !important",
                    display: "flex",
                    alignItems: "flex-start !important",
                  },
                  "& .MuiDataGrid-row": {
                    minHeight: "auto !important",
                  },
                  "& .MuiDataGrid-cell--withRenderer": {
                    alignItems: "flex-start !important",
                  },
                }}
              />
            </Paper>

            {/* Add New Field Button */}
            <Button
              variant="outlined"
              onClick={() => setShowAddField(true)}
              sx={{
                mt: 1,
                borderColor: "var(--color-primary)",
                color: "var(--color-secondary)",
              }}
            >
              Add Field
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: "0px" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ minWidth: "100px", fontWeight: "bold", color: "var(--color-primary)" }}
            >
              Notes
            </Typography>
          </Box>

          {/* Notes Display Area - Scrollable */}
          <Paper
            sx={{
              maxHeight: 164,
              width: "100%",
              boxShadow: 2,
              mb: 3,
              overflow: "auto",
            }}
          >
            <DataGrid
              rows={notes}
              columns={columns}
              autoHeight
              getRowHeight={() => 'auto'}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    id: false, // 👈 hides the 'id' column
                  },
                },
                pagination: false,
              }}
              pagination={false}
              disableRowSelectionOnClick
              hideFooter
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "14px",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  py: 0.1,
                  minHeight: "auto !important",
                },
                "& .MuiDataGrid-row": {
                  minHeight: "auto !important",
                },
              }}
            />
          </Paper>

          {/* Add Note Input at Bottom */}
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                multiline
                placeholder="Add New Note"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                error={newNote.length > 160}
                inputProps={{ maxLength: 160 }}
                variant="outlined"
                size="small"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    minHeight: "56px !important",
                    height: "fit-content !important",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px !important", // Set text size to 14px
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddNote}
                disabled={!newNote.trim() || newNote.length > 160}
                sx={{
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-secondary)" },
                  minWidth: "100px",
                  height: "40px",
                }}
              >
                Add Note
              </Button>
            </Box>
            {/* <Typography
              variant="caption"
              color={newNote.length > 160 ? "error" : "text.secondary"}
              sx={{ ml: 1 }}
            >
              {newNote.length}/160 characters
            </Typography> */}
          </Box>
        </div>
      </Box>

      {/* Add Custom Field Modal */}
      <Dialog
        open={showAddField}
        onClose={handleCancelAdd}
        PaperProps={{
          sx: {
            minWidth: 300,
            maxWidth: 450,
            p: 1,
            position: "relative",
          },
        }}
      >
        {/* Absolute positioned close button */}
        <IconButton
          aria-label="close"
          onClick={handleCancelAdd}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--color-primary)",
            zIndex: 1,
          }}
        >
          <Cancel fontSize="small" />
        </IconButton>

        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            pb: 0,
            mb: 1,
            pr: 4, // Add padding to right to accommodate absolute close button
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            color="var(--color-primary)"
          >
            Add Custom Field
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box>
            <TextField
              fullWidth
              label="Field Key"
              placeholder="e.g., Department, Priority, Location"
              value={newFieldKey}
              onChange={(e) => setNewFieldKey(e.target.value)}
              inputProps={{ maxLength: 30 }}
              required
              sx={{ mb: 2, mt: 1 }}
              variant="outlined"
              size="medium"
              autoFocus
            />
            <TextField
              fullWidth
              label="Field Value"
              placeholder="e.g., IT Support, High, New York"
              value={newFieldValue}
              onChange={(e) => setNewFieldValue(e.target.value)}
              required
              inputProps={{ maxLength: 30 }}
              sx={{ mb: 2 }}
              variant="outlined"
              size="medium"
            />
            <Button
              fullWidth
              onClick={handleAddNewField}
              variant="contained"
              sx={{
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
            >
              Add Field
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ConsumerDetails;
